// notificationStore.ts
//
// Reads, marks-read, and polls the user's D2P notifications.
// Backed by /api/d2p/user/notifications (Bearer auth).
//
// Polls every 30 s while a user is logged in. Stops polling on logout.

import { makeAutoObservable, runInAction } from 'mobx';
import { hitApi } from '../database/services/ApiHelper';
import { API } from '../database/apis';
import { userStore } from './userStore';

export interface NotificationEntry {
  id: string;
  type: string;
  title: string;
  description?: string | null;
  icon?: string | null;
  link?: string | null;
  read: boolean;
  createdAt: string;
  metadata?: Record<string, any> | null;
}

const POLL_INTERVAL_MS = 30_000;

class NotificationStore {
  items: NotificationEntry[] = [];
  unreadCount = 0;
  loading = false;
  // Set to true while a mark-read mutation is in flight so the UI can
  // disable the buttons.
  mutating = false;

  private pollTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Call once on app start (e.g. after Splash hydration). Idempotent —
  // calling again is a no-op while polling is active.
  startPolling() {
    if (this.pollTimer) return;
    // Kick off immediately so the bell badge is right after a refresh.
    this.fetch().catch(() => {});
    this.pollTimer = setInterval(() => {
      // Only poll while the user is signed in; the server requires Bearer
      // auth and would 401 every 30 s otherwise.
      if (!userStore.isLoggedIn) return;
      this.fetch().catch(() => {});
    }, POLL_INTERVAL_MS);
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  async fetch() {
    if (!userStore.authToken) {
      // Anonymous device sessions don't have notifications.
      runInAction(() => {
        this.items = [];
        this.unreadCount = 0;
      });
      return;
    }
    try {
      runInAction(() => { this.loading = true; });
      const res = await hitApi({
        endpoint: `${API.NOTIFICATIONS}?limit=30`,
        method: 'GET',
        token: userStore.authToken,
      });
      runInAction(() => {
        this.items = Array.isArray(res?.notifications) ? res.notifications : [];
        this.unreadCount = typeof res?.unreadCount === 'number' ? res.unreadCount : 0;
        this.loading = false;
      });
    } catch (err) {
      // Silent — the bell just shows the last known count. We don't want a
      // 401 during background poll to throw a visible error.
      runInAction(() => { this.loading = false; });
    }
  }

  async markAsRead(ids: string[]) {
    if (!userStore.authToken || ids.length === 0) return;
    // Optimistic update — flip locally first, then sync.
    const targets = new Set(ids);
    const prevUnread = this.unreadCount;
    runInAction(() => {
      this.mutating = true;
      this.items = this.items.map((n) =>
        targets.has(n.id) && !n.read ? { ...n, read: true } : n,
      );
      this.unreadCount = Math.max(0, this.unreadCount - ids.length);
    });
    try {
      await hitApi({
        endpoint: API.NOTIFICATIONS,
        method: 'PATCH',
        body: { notificationIds: ids },
        token: userStore.authToken,
      });
    } catch {
      // Roll back on failure.
      runInAction(() => {
        this.unreadCount = prevUnread;
      });
      // Refetch to resync.
      this.fetch().catch(() => {});
    } finally {
      runInAction(() => { this.mutating = false; });
    }
  }

  async markAllAsRead() {
    if (!userStore.authToken) return;
    const prevItems = [...this.items];
    const prevUnread = this.unreadCount;
    runInAction(() => {
      this.mutating = true;
      this.items = this.items.map((n) => ({ ...n, read: true }));
      this.unreadCount = 0;
    });
    try {
      await hitApi({
        endpoint: API.NOTIFICATIONS,
        method: 'PATCH',
        body: { markAllAsRead: true },
        token: userStore.authToken,
      });
    } catch {
      runInAction(() => {
        this.items = prevItems;
        this.unreadCount = prevUnread;
      });
    } finally {
      runInAction(() => { this.mutating = false; });
    }
  }

  clear() {
    this.items = [];
    this.unreadCount = 0;
  }
}

export const notificationStore = new NotificationStore();
